import { useParams, Link } from "react-router-dom";
import { movies } from "@/data/movies";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Play, Star, Clock, Calendar } from "lucide-react";

export default function MovieDetail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          <Link to="/">
            <Button variant="ghost" className="mb-6 gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">Movie not found</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Link to="/">
          <Button variant="ghost" className="mb-6 gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div className="md:col-span-1">
            <img
              src={movie.poster}
              alt={movie.title}
              className="w-full rounded-lg shadow-lg mb-6"
            />
            <Button size="lg" className="w-full gap-2 mb-4">
              <Play className="w-5 h-5 fill-current" />
              Play Movie
            </Button>
            <Button size="lg" variant="outline" className="w-full">
              Add to Favorites
            </Button>
          </div>

          <div className="md:col-span-2">
            <h1 className="text-4xl font-bold text-foreground mb-4">{movie.title}</h1>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="font-semibold">{movie.rating}/10</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <span>{movie.year}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-muted-foreground" />
                <span>{movie.duration} min</span>
              </div>
              <Badge variant="outline">{movie.maturityRating}</Badge>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-2">Genres</h3>
              <div className="flex flex-wrap gap-2">
                {movie.genre.map((g) => (
                  <Badge key={g} variant="secondary">
                    {g}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-2">Synopsis</h3>
              <p className="text-muted-foreground text-base leading-relaxed">
                {movie.description}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-2">Director</h3>
              <p className="text-muted-foreground">{movie.director}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Cast</h3>
              <div className="grid grid-cols-2 gap-3">
                {movie.cast.map((actor) => (
                  <p key={actor} className="text-muted-foreground">
                    {actor}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
